import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Exam, ExamResult, UserResponse } from './types';
import * as db from './services/dataService';

// Hooks
import { useAuth } from './hooks/useAuth';
import { useExamData } from './hooks/useExamData';

// Layout Components
import Header from './components/layout/Header';

// Page Components
import HomePage from './components/pages/HomePage';
import TestInterface from './components/TestInterface';
import ResultView from './components/ResultView';
import AdminPanel from './components/AdminPanel';
import AuthModal from './components/AuthModal';
import UserDashboard from './components/UserDashboard';
import PricingModal from './components/PricingModal';
import CustomGenerator from './components/CustomGenerator';
import UserProfile from './components/UserProfile';
import DailyCASelection from './components/DailyCASelection';
import ResetPassword from './components/ResetPassword';

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Custom hooks for state management
  const {
    user,
    setUser,
    showAuthModal,
    setShowAuthModal,
    authModalConfig,
    setAuthModalConfig,
    handleLoginSuccess,
    handleLogout,
    handleUpgradeSubscription
  } = useAuth();

  const {
    categories,
    exams,
    isLoadingData,
    handleAddCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    handleAddExam,
    handleUpdateExam,
    handleDeleteExam
  } = useExamData();

  // Local state for exam flow
  const [activeExam, setActiveExam] = useState<Exam | null>(null);
  const [examResult, setExamResult] = useState<ExamResult | null>(null);
  const [userResponses, setUserResponses] = useState<UserResponse[]>([]);
  const [dailyCATests, setDailyCATests] = useState<Exam[]>([]);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Restore activeExam on page refresh if path is /exam/:examId and user is authenticated
  React.useEffect(() => {
    const match = location.pathname.match(/\/exam\/([^/]+)/);
    if (match && !activeExam && exams.length > 0 && !isLoadingData) {
      const examId = match[1];
      const foundExam = exams.find((e) => e.id === examId);
      if (foundExam && user) {
        setActiveExam(foundExam);
      }
    }
  }, [location.pathname, activeExam, exams, isLoadingData, user]);

  // Daily CA Logic
  const today = new Date();
  const dateString = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const dateISO = today.toISOString().split('T')[0];
  const defaultDailyExamId = `daily-ca-${dateISO}`;

  // Handle clicking the Daily CA banner/icon
  const handleDailyCAClick = () => {
    if (user && user.role !== 'admin' && user.allowedCategories && user.allowedCategories.length > 0) {
      if (!user.allowedCategories.includes('ca')) {
        alert("Access Denied: You do not have access to the Current Affairs category.");
        return;
      }
    }

    const todaysTests = exams.filter(e =>
      e.category === 'ca' && (e.id.includes(dateISO) || e.title.includes(dateString) || e.id === defaultDailyExamId)
    );

    if (todaysTests.length > 1) {
      setDailyCATests(todaysTests);
      navigate('/daily-ca');
    } else if (todaysTests.length === 1) {
      startExam(todaysTests[0].id);
    } else {
      startExam(defaultDailyExamId);
    }
  };

  const startExam = (examId: string) => {
    // 1. Check Login
    if (!user) {
      setAuthModalConfig({ requireCompletion: false, userToComplete: null });
      setShowAuthModal(true);
      return;
    }

    const exam = exams.find((e) => e.id === examId);

    if (!exam) {
      if (examId === defaultDailyExamId) {
        alert("Today's Current Affairs test hasn't been uploaded yet. Please check back later!");
      } else {
        alert("Test not found.");
      }
      return;
    }

    // Check category access restriction for non-admins
    if (user && user.role !== 'admin' && user.allowedCategories && user.allowedCategories.length > 0) {
      if (!user.allowedCategories.includes(exam.category)) {
        alert("Access Denied: You do not have access to this exam category.");
        return;
      }
    }

    // Live Exam Validation
    if (exam.startTime) {
      const now = new Date();
      const start = new Date(exam.startTime);
      const end = exam.endTime ? new Date(exam.endTime) : new Date(start.getTime() + exam.durationMinutes * 60000 * 2);

      if (now < start) {
        alert(`This Live Mock starts on ${start.toLocaleString()}. Please wait.`);
        return;
      }

      if (now > end) {
        alert("The live window for this exam has ended. You are attempting it as a practice test.");
      }
    }

    // 2. Check Premium Access
    if (exam.isPremium && user.subscriptionTier === 'free') {
      setShowPricingModal(true);
      return;
    }

    // 3. Start
    setActiveExam(exam);
    navigate(`/exam/${exam.id}`);
  };

  const handleExamSubmit = async (responses: UserResponse[], timeTaken: number) => {
    if (!activeExam) return;

    let correct = 0;
    let wrong = 0;
    let unattempted = 0;
    let score = 0;

    responses.forEach((resp) => {
      const question = activeExam.questions.find((q) => q.id === resp.questionId);
      if (!question) return;

      if (resp.selectedOptionId === null) {
        unattempted++;
      } else if (resp.selectedOptionId === question.correctOptionId) {
        correct++;
        score += activeExam.positiveMarks;
      } else {
        wrong++;
        score -= activeExam.negativeMarks;
      }
    });

    const result: ExamResult = {
      examId: activeExam.id,
      score,
      totalQuestions: activeExam.totalQuestions,
      correctCount: correct,
      wrongCount: wrong,
      unattemptedCount: unattempted,
      accuracy: responses.filter(r => r.selectedOptionId).length > 0
        ? (correct / responses.filter(r => r.selectedOptionId).length) * 100
        : 0,
      timeTaken,
    };

    setExamResult(result);
    setUserResponses(responses);
    navigate('/result');

    // Save attempt if user is logged in
    if (user) {
      await db.saveAttempt(user.id, result);
    }
  };

  const handleLogoutAndNavigate = async () => {
    await handleLogout();
    navigate('/');
  };

  const openLoginModal = () => {
    setAuthModalConfig({ requireCompletion: false, userToComplete: null });
    setShowAuthModal(true);
  };

  // Determine if we should show the header (not during test)
  const showHeader = !location.pathname.startsWith('/exam/') && !location.pathname.startsWith('/result');

  return (
    <div className="min-h-screen bg-transparent font-sans">
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
        requireProfileCompletion={authModalConfig.requireCompletion}
        userToComplete={authModalConfig.userToComplete}
      />

      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        onUpgrade={handleUpgradeSubscription}
        user={user}
      />

      {showHeader && (
        <Header
          user={user}
          onLogout={handleLogoutAndNavigate}
          onLoginClick={openLoginModal}
          onUpgradeClick={() => setShowPricingModal(true)}
        />
      )}

      <Routes>
        {/* Home Page */}
        <Route
          path="/"
          element={
            <HomePage
              categories={categories}
              exams={exams}
              user={user}
              isLoadingData={isLoadingData}
              onStartExam={startExam}
              onShowPricing={() => setShowPricingModal(true)}
              onDailyCAClick={handleDailyCAClick}
              onUpdateUser={setUser}
            />
          }
        />

        {/* Test Interface */}
        <Route
          path="/exam/:examId"
          element={
            activeExam ? (
              <TestInterface
                exam={activeExam}
                onSubmit={handleExamSubmit}
                onExit={() => navigate('/')}
              />
            ) : (
              <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                  <i className="fas fa-exclamation-circle text-4xl text-slate-300 mb-4"></i>
                  <p className="text-slate-500">No exam selected. Please choose an exam from the home page.</p>
                  <button
                    onClick={() => navigate('/')}
                    className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600"
                  >
                    Go Home
                  </button>
                </div>
              </div>
            )
          }
        />

        {/* Result View */}
        <Route
          path="/result"
          element={
            activeExam && examResult ? (
              <ResultView
                result={examResult}
                exam={activeExam}
                responses={userResponses}
                onBack={() => navigate('/')}
              />
            ) : (
              <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                  <p className="text-slate-500">No result available.</p>
                  <button
                    onClick={() => navigate('/')}
                    className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600"
                  >
                    Go Home
                  </button>
                </div>
              </div>
            )
          }
        />

        {/* User Dashboard */}
        <Route
          path="/dashboard"
          element={
            user ? (
              <UserDashboard
                user={user}
                onStartExam={() => navigate('/')}
                onStartCustomExam={() => navigate('/dashboard/custom')}
                onStartCustomExamInstance={(exam) => {
                  setActiveExam(exam);
                  navigate(`/exam/${exam.id}`);
                }}
              />
            ) : (
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                  <p className="text-slate-500">Please login to view your dashboard.</p>
                  <button
                    onClick={openLoginModal}
                    className="mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600"
                  >
                    Login
                  </button>
                </div>
              </div>
            )
          }
        />

        {/* Custom Generator */}
        <Route
          path="/dashboard/custom"
          element={
            <CustomGenerator
              categories={user?.role === 'admin' ? categories : categories.filter(c => user?.allowedCategories?.includes(c.id))}
              onStartExam={(exam) => {
                setActiveExam(exam);
                navigate(`/exam/${exam.id}`);
              }}
              onCancel={() => navigate('/dashboard')}
            />
          }
        />

        {/* User Profile */}
        <Route
          path="/profile"
          element={
            user ? (
              <UserProfile
                user={user}
                onUpdateUser={setUser}
                onBack={() => navigate('/dashboard')}
              />
            ) : (
              <div className="flex items-center justify-center min-h-[60vh]">
                <p className="text-slate-500">Please login to view your profile.</p>
              </div>
            )
          }
        />

        {/* Daily CA Selection */}
        <Route
          path="/daily-ca"
          element={
            <DailyCASelection
              tests={dailyCATests}
              onSelectTest={(id) => startExam(id)}
              onBack={() => navigate('/')}
            />
          }
        />

        {/* Reset Password */}
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Admin Panel */}
        <Route
          path="/admin/*"
          element={
            user?.role === 'admin' ? (
              <AdminPanel
                categories={categories}
                exams={exams}
                onAddCategory={handleAddCategory}
                onUpdateCategory={handleUpdateCategory}
                onDeleteCategory={handleDeleteCategory}
                onAddExam={handleAddExam}
                onUpdateExam={handleUpdateExam}
                onDeleteExam={handleDeleteExam}
              />
            ) : (
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="bg-red-50 text-red-600 p-8 rounded-xl text-center max-w-md">
                  <i className="fas fa-lock text-4xl mb-4"></i>
                  <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
                  <p>You need Administrator privileges to view this page.</p>
                  <button
                    onClick={() => navigate('/')}
                    className="mt-6 px-6 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 transition-colors"
                  >
                    Return Home
                  </button>
                </div>
              </div>
            )
          }
        />
      </Routes>
    </div>
  );
};

export default App;